import type { Locale } from "./i18n";
import webToAppConfig from "../web-to-app.config.json" with { type: "json" };

export const WEBSITE_ORIGIN = "https://backtest.click";
export const WEB_TO_APP_ORIGIN = "https://www.backtest.click";
export const APP_CLIP_ORIGIN = WEBSITE_ORIGIN;
export const APP_STORE_PRODUCT_URL = webToAppConfig.appStoreURL;
export const APP_STORE_PROVIDER_TOKEN = webToAppConfig.appStoreProviderToken;
export const APP_CLIP_ROUTES = Object.freeze([...webToAppConfig.appClipRoutes]);

const supportedLocales = new Set<Locale>([
  "en", "vi", "hi", "ur", "th", "bn", "id", "fil", "es-419",
  "pt-BR", "ja", "ko", "ru", "tr", "uk",
]);
const supportedSections = new Set([
  "backtest", "pricing", "results", "history", "bots", "account", "settings",
]);
const supportedExperiences = new Set([
  "crypto-btc", "forex-xauusd", "visual-builder", "paper-bot",
]);
const supportedAppClipRoutes = new Set<string>(APP_CLIP_ROUTES);
const supportedStrategies = new Map([
  "MA Cross", "Pullback", "Breakout", "Engulfing", "Pin Bar", "Inside Bar",
  "Sweep", "RSI", "Bollinger", "MACD", "Trend+", "Vol Break", "Mean+", "MTF",
  "Dual MA 50/200", "Donchian 55/20", "Multi-Horizon Trend",
  "ADX Directional Lab", "Relative Breakout Lab", "Range Reversion Lab",
  "btc-ema-rsi", "btc-macd-futures", "Visual Strategy Builder",
].map((value) => [value.toLowerCase(), value]));

export type WebToAppPlacement = "topbar" | "hero" | "pricing" | "article_footer";

export type NavigatorDeviceHints = {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
};

/**
 * iPadOS can request a desktop-class user agent, so viewport width and the
 * user-agent string alone are not reliable. A touch-capable Mac signature is
 * treated as iPadOS; a real Mac remains on the desktop handoff path.
 */
export function isIOSOrIPadOSDevice(hints: NavigatorDeviceHints): boolean {
  const userAgent = hints.userAgent || "";
  const platform = hints.platform || "";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;

  const looksLikeMac = /Macintosh|Mac OS X/i.test(userAgent) || /^Mac/i.test(platform);
  return looksLikeMac && (hints.maxTouchPoints || 0) > 1;
}

/** Detect a real Mac without misclassifying iPadOS desktop-mode user agents. */
export function isMacOSDesktopDevice(hints: NavigatorDeviceHints): boolean {
  const userAgent = hints.userAgent || "";
  const platform = hints.platform || "";
  const looksLikeMac = /Macintosh|Mac OS X/i.test(userAgent) || /^Mac/i.test(platform);
  return looksLikeMac && !isIOSOrIPadOSDevice(hints);
}

export type WebToAppStrategyContext = {
  symbol?: string;
  timeframe?: string;
  period?: string;
  strategy?: string;
  direction?: string;
  mode?: string;
};

export type WebToAppLinkInput = {
  pathname: string;
  source: string;
  medium: string;
  campaign: string;
  locale: Locale;
  slug?: string;
  strategy?: WebToAppStrategyContext;
};

function safeToken(value: string | undefined, maximumLength: number): string | undefined {
  const normalized = value?.trim();
  if (!normalized || normalized.length > maximumLength || !/^[A-Za-z0-9._-]+$/.test(normalized)) return undefined;
  return normalized;
}

function safeSlug(value: string | undefined): string | undefined {
  const normalized = value?.trim().toLowerCase();
  if (!normalized || normalized.length > 96 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalized)) return undefined;
  return normalized;
}

function safeSymbol(value: string | undefined): string | undefined {
  const normalized = value?.trim().toUpperCase();
  if (!normalized || normalized.length > 24 || !/^[A-Z0-9./=^_-]+$/.test(normalized)) return undefined;
  return normalized;
}

function safeStrategy(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  if (!normalized || normalized.length > 64 || /[<>{}\[\]\\"'`\r\n\t]/.test(normalized)) return undefined;
  return supportedStrategies.get(normalized.toLowerCase());
}

function allowedValue(value: string | undefined, allowed: ReadonlySet<string>, transform: (value: string) => string): string | undefined {
  if (!value) return undefined;
  const normalized = transform(value.trim());
  return allowed.has(normalized) ? normalized : undefined;
}

function normalizedPathname(pathname: string): string {
  const candidate = new URL(pathname, WEBSITE_ORIGIN);
  if (candidate.origin !== WEBSITE_ORIGIN && candidate.origin !== WEB_TO_APP_ORIGIN) return "/backtest";
  if (supportedAppClipRoutes.has(candidate.pathname)) return candidate.pathname;
  const segments = candidate.pathname.split("/").filter(Boolean);
  if (segments.length && supportedLocales.has(segments[0] as Locale)) segments.shift();
  if (!segments.length) return candidate.pathname;
  if (segments[0] === "learn" && segments.length === 2 && safeSlug(segments[1])) return candidate.pathname;
  if (segments[0] === "open" && segments.length === 2 && supportedExperiences.has(segments[1])) return candidate.pathname;
  if (supportedSections.has(segments[0]) && (segments.length === 1 || segments[0] === "backtest")) return candidate.pathname;
  return "/backtest";
}

export function localizedSectionPath(locale: Locale, section: "backtest" | "pricing"): string {
  return `/${locale}/${section}`;
}

export function buildWebToAppURL(input: WebToAppLinkInput): string {
  const pathname = normalizedPathname(input.pathname);
  const url = new URL(pathname, supportedAppClipRoutes.has(pathname)
    ? APP_CLIP_ORIGIN
    : WEB_TO_APP_ORIGIN);
  const source = safeToken(input.source, 40);
  const medium = safeToken(input.medium, 40);
  const campaign = safeToken(input.campaign, 64);
  const slug = safeSlug(input.slug);
  const articleContext = webToAppConfig.articleStrategyContexts[
    slug as keyof typeof webToAppConfig.articleStrategyContexts
  ];
  const strategy = { ...articleContext, ...input.strategy };

  if (source) url.searchParams.set("utm_source", source);
  if (medium) url.searchParams.set("utm_medium", medium);
  if (campaign) url.searchParams.set("utm_campaign", campaign);
  url.searchParams.set("st_locale", input.locale);
  const isExperienceRoute = supportedAppClipRoutes.has(url.pathname)
    || url.pathname.startsWith("/open/");
  if (isExperienceRoute) {
    if (source) url.searchParams.set("source", source);
    url.searchParams.set("locale", input.locale);
  }
  if (slug) url.searchParams.set("st_slug", slug);

  const symbol = safeSymbol(strategy.symbol);
  const timeframe = allowedValue(strategy.timeframe, new Set(["5m", "15m", "30m", "1h", "4h", "1d"]), (value) => value.toLowerCase());
  const period = allowedValue(strategy.period, new Set(["1M", "3M", "6M", "1Y", "Custom"]), (value) => value === "custom" || value === "CUSTOM" ? "Custom" : value.toUpperCase());
  const strategyName = safeStrategy(strategy.strategy) || (supportedAppClipRoutes.has(url.pathname) ? "btc-macd-futures" : "");
  const direction = allowedValue(strategy.direction, new Set(["long", "short", "both"]), (value) => value.toLowerCase());
  const mode = allowedValue(strategy.mode, new Set(["auto", "manual", "builder"]), (value) => value.toLowerCase());
  if (symbol) url.searchParams.set("symbol", symbol);
  if (timeframe) url.searchParams.set("timeframe", timeframe);
  if (period) url.searchParams.set("period", period);
  if (strategyName) url.searchParams.set("strategy", strategyName);
  if (direction) url.searchParams.set("direction", direction);
  if (mode) url.searchParams.set("mode", mode);
  return url.toString();
}

export function buildAppStoreCampaignURL(
  campaign: string,
  placement: WebToAppPlacement,
  providerToken: string = APP_STORE_PROVIDER_TOKEN,
): string {
  const url = new URL(APP_STORE_PRODUCT_URL);
  // Apple's campaign token limit includes our placement suffix.
  const campaignToken = safeToken(`${campaign}_${placement}`, 30);
  if (/^[0-9]{1,20}$/.test(providerToken)) url.searchParams.set("pt", providerToken);
  if (campaignToken) url.searchParams.set("ct", campaignToken);
  url.searchParams.set("mt", "8");
  return url.toString();
}

export function webToAppAnalyticsParameters(input: WebToAppLinkInput, placement: WebToAppPlacement) {
  const slug = safeSlug(input.slug);
  const strategy = {
    ...webToAppConfig.articleStrategyContexts[slug as keyof typeof webToAppConfig.articleStrategyContexts],
    ...input.strategy,
  };
  const safeStrategyContext = [
    safeSymbol(strategy.symbol),
    safeStrategy(strategy.strategy),
    safeToken(strategy.timeframe, 8),
  ].some(Boolean);
  const parameters: Record<string, string | boolean> = {
    placement,
    source: safeToken(input.source, 40) || "website",
    campaign: safeToken(input.campaign, 64) || "web_to_app",
    locale: input.locale,
    has_strategy_context: safeStrategyContext,
  };
  if (slug) parameters.slug = slug;
  return parameters;
}
